import { signedPct, signedUnits } from "~/components/demand-2/shared/demandBreakdownShared"
import { AnswerPicker } from "~/components/demand-2/shared/FramingAnswerPicker"
import { useScopedFramingAnswer } from "~/components/demand-2/shared/useScopedFramingAnswer"
import { type AnswerScope, type FramingNode, WHOLE_COLLECTION_NODE } from "~/utils/demandFraming"
import type { InsightScalars } from "~/utils/demandInsightValues"

/** The phase-out sell-down summed over one set of variants: the whole route, or one node. */
export type PhasingOutFigures = {
  /** Their baseline, what ignoring the phase-out plans on. */
  baseline: number
  /** ≤ 0 whatever the answer: what planning the sell-down takes off. */
  sellDownUnits: number
}

export const sumPhasingOutFigures = (
  variants: readonly { variantId: string }[],
  scalarsFor: (variantId: string) => InsightScalars | null | undefined,
): PhasingOutFigures => {
  const out: PhasingOutFigures = { baseline: 0, sellDownUnits: 0 }
  for (const { variantId } of variants) {
    const s = scalarsFor(variantId)
    if (s?.phasingOutUnits == null || s.phasingOutUnits === 0) continue
    out.baseline += s.baseline ?? 0
    out.sellDownUnits += s.phasingOutUnits
  }
  return out
}

/** The `phasingOut` question, asked under the chart of the variants it moves. */
const PhasingOutAnswer = ({
  node: nodeProp = WHOLE_COLLECTION_NODE,
  scope: scopeProp,
  figures,
}: {
  /** Defaults to the whole collection; a node falls back to the collection's answer. */
  node?: FramingNode
  /** Overrides `node`, e.g. one variant from the Total page. */
  scope?: AnswerScope
  figures: PhasingOutFigures
}) => {
  const read = useScopedFramingAnswer("phasingOut", nodeProp, scopeProp)
  if (read == null) return null
  const { collectionId, scope, answer, inherited, isDefault } = read
  // What each answer comes to on this scope.
  const impactOf = (optionId: string): React.ReactNode => {
    if (figures.sellDownUnits === 0) return null
    if (optionId === "ignore") {
      return <span className="paragraph-sm text-fg-subtle">No change to the baseline</span>
    }
    return (
      <span className="paragraph-sm text-fg-subtle">
        <span className="text-fg-error-icon">
          {signedUnits(figures.sellDownUnits)} units
          {figures.baseline > 0 ? ` · ${signedPct(figures.sellDownUnits / figures.baseline)}` : ""}
        </span>{" "}
        on the phasing-out variants&apos; baseline
      </span>
    )
  }
  return (
    <AnswerPicker
      collectionId={collectionId}
      question="phasingOut"
      answer={answer}
      scope={scope}
      inherited={inherited}
      isDefault={isDefault}
      optionFooter={impactOf}
    />
  )
}

export default PhasingOutAnswer
